import React, { useState } from "react";
import {
	useStripe,
	useElements,
	PaymentElement,
	AddressElement,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";

interface PendingPaymentInstructions {
	status: string;
	message: string;
	expiresAt: string | null;
	hostedVoucherUrl: string | null;
	storeFields: Array<{
		storeName: string;
		entries: Array<{
			label: string;
			value: string;
		}>;
	}>;
}

interface StripePaymentFormProps {
	clientSecret: string;
	orderId: number;
	amount: number;
	onPaymentSuccess: (orderId: number) => void;
	onPaymentPending: (orderId: number) => void;
	onPaymentError: (error: string) => void;
}

const formatInstructionLabel = (value: string): string =>
	value
		.replace(/_/g, " ")
		.replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeDisplayValue = (value: unknown): string | null => {
	if (typeof value === "string" && value.trim() !== "") {
		return value;
	}

	if (typeof value === "number" || typeof value === "boolean") {
		return String(value);
	}

	return null;
};

const extractPendingInstructions = (
	paymentIntent: any
): PendingPaymentInstructions | null => {
	const konbiniDetails = paymentIntent?.next_action?.konbini_display_details;
	const stores = konbiniDetails?.stores ?? {};
	const storeFields = Object.entries(stores)
		.map(([storeName, details]) => {
			const entries = Object.entries((details as Record<string, unknown>) ?? {})
				.map(([key, value]) => ({
					label: formatInstructionLabel(key),
					value: normalizeDisplayValue(value),
				}))
				.filter(
					(entry): entry is { label: string; value: string } =>
						entry.value !== null
				);

			if (entries.length === 0) {
				return null;
			}

			return {
				storeName: formatInstructionLabel(storeName),
				entries,
			};
		})
		.filter(
			(
				store
			): store is {
				storeName: string;
				entries: Array<{ label: string; value: string }>;
			} => store !== null
		);

	const expiresAt =
		typeof konbiniDetails?.expires_at === "number"
			? new Date(konbiniDetails.expires_at * 1000).toLocaleString("ja-JP")
			: null;

	const hostedVoucherUrl = normalizeDisplayValue(
		konbiniDetails?.hosted_voucher_url
	);

	if (!konbiniDetails && paymentIntent?.status !== "requires_action") {
		return null;
	}

	return {
		status: paymentIntent?.status ?? "processing",
		message:
			konbiniDetails
				? "コンビニ支払い情報を発行しました。以下の案内に従ってお支払いを完了してください。"
				: "決済はまだ完了していません。画面の案内を確認して、必要な手続きを完了してください。",
		expiresAt,
		hostedVoucherUrl,
		storeFields,
	};
};

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
	clientSecret,
	orderId,
	amount,
	onPaymentSuccess,
	onPaymentPending,
	onPaymentError,
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [pendingInstructions, setPendingInstructions] =
		useState<PendingPaymentInstructions | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!stripe || !elements) {
			// Stripe.js hasn't yet loaded.
			return;
		}

		setIsLoading(true);
		setErrorMessage(null);
		setPendingInstructions(null);

		try {
			const { error, paymentIntent } = await stripe.confirmPayment({
				elements,
				confirmParams: {
					return_url: `${window.location.origin}/payment-success?order_id=${orderId}`,
				},
				redirect: "if_required",
			});

			if (error) {
				setErrorMessage(error.message || "決済処理中にエラーが発生しました。");
				onPaymentError(error.message || "決済処理中にエラーが発生しました。");
			} else if (paymentIntent && paymentIntent.status === "succeeded") {
				toast.success("決済が完了しました！");
				onPaymentSuccess(orderId);
			} else if (paymentIntent && paymentIntent.status === "processing") {
				toast.info("支払いは受け付け済みです。反映状況を確認します。");
				onPaymentPending(orderId);
			} else if (paymentIntent && paymentIntent.status === "requires_action") {
				const instructions = extractPendingInstructions(paymentIntent);

				setPendingInstructions(instructions);
				toast.info(
					instructions?.hostedVoucherUrl || instructions?.storeFields.length
						? "支払い情報を表示しました。案内に従ってお支払いを完了してください。"
						: "追加の手続きが必要です。画面の案内をご確認ください。"
				);
			} else {
				const fallbackMessage = paymentIntent?.status
					? `決済はまだ完了していません（${paymentIntent.status}）。状況確認画面へ移動します。`
					: "決済ステータスが確認できないため、状況確認画面へ移動します。";
				toast.info(fallbackMessage);
				onPaymentPending(orderId);
			}
		} catch (err) {
			console.error("Payment error:", err);
			setErrorMessage("決済処理中にエラーが発生しました。");
			onPaymentError("決済処理中にエラーが発生しました。");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
				<h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">お支払い情報</h3>
				<PaymentElement
					options={{
						paymentMethodOrder: ["card", "konbini"],
						defaultValues: {
							billingDetails: {
								name: "",
								phone: "",
								email: "",
							},
						},
					}}
				/>

				<div className="mt-6">
					<h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">請求先住所</h3>
					<AddressElement options={{ mode: "billing" }} />
				</div>
			</div>

			{pendingInstructions && (
				<div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 space-y-4">
					<div>
						<h3 className="text-lg font-semibold text-amber-900 dark:text-amber-200">
							支払い手続きの続きがあります
						</h3>
						<p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
							{pendingInstructions.message}
						</p>
					</div>

					{pendingInstructions.expiresAt && (
						<p className="text-sm text-amber-900 dark:text-amber-200">
							支払い期限: {pendingInstructions.expiresAt}
						</p>
					)}

					{pendingInstructions.hostedVoucherUrl && (
						<a
							href={pendingInstructions.hostedVoucherUrl}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium transition-colors"
						>
							支払い番号を表示する
						</a>
					)}

					{pendingInstructions.storeFields.length > 0 && (
						<div className="space-y-3">
							{pendingInstructions.storeFields.map((store) => (
								<div
									key={store.storeName}
									className="bg-white dark:bg-gray-800 rounded-lg border border-amber-100 dark:border-amber-800 p-4"
								>
									<p className="font-semibold text-gray-900 dark:text-white">
										{store.storeName}
									</p>
									<div className="mt-3 space-y-2 text-sm">
										{store.entries.map((entry) => (
											<div
												key={`${store.storeName}-${entry.label}`}
												className="flex items-start justify-between gap-4"
											>
												<span className="text-gray-500 dark:text-gray-400">
													{entry.label}
												</span>
												<span className="font-medium text-gray-900 dark:text-white break-all text-right">
													{entry.value}
												</span>
											</div>
										))}
									</div>
								</div>
							))}
						</div>
					)}

					<div className="flex flex-wrap gap-3">
						<button
							type="button"
							onClick={() => onPaymentPending(orderId)}
							className="px-4 py-2 rounded-lg border border-amber-300 dark:border-amber-600 text-amber-900 dark:text-amber-200 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
						>
							決済状況を確認する
						</button>
					</div>
				</div>
			)}

			{errorMessage && (
				<div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 dark:border-red-400 p-4 rounded">
					<p className="text-red-700 dark:text-red-400">{errorMessage}</p>
				</div>
			)}

			<div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
				<p className="text-lg font-bold text-gray-900 dark:text-white">合計: ¥{amount.toLocaleString()}</p>
				<button
					type="submit"
					disabled={!stripe || isLoading || !!pendingInstructions}
					className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2.5 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
				>
					{isLoading
						? "処理中..."
						: pendingInstructions
						? "支払い情報を確認してください"
						: "今すぐ支払う"}
				</button>
			</div>
		</form>
	);
};

export default StripePaymentForm;
